import { env } from '@/lib/env'
import { ApiError, networkProblem, parseProblem } from '@/lib/problem'

/* ------------------------------------------------------------------ token */

/**
 * Access token **bellekte** tutulur. `localStorage`/`sessionStorage`'a asla yazılmaz
 * (AGENTS.md §4). Sekme yenilendiğinde token kaybolur; açılışta `/auth/refresh` ile
 * httpOnly cookie üzerinden yeniden alınır.
 */
let accessToken: string | null = null

export const tokenStore = {
  get(): string | null {
    return accessToken
  },
  set(token: string | null): void {
    accessToken = token
  },
  clear(): void {
    accessToken = null
  },
}

type UnauthorizedHandler = () => void

let unauthorizedHandler: UnauthorizedHandler = () => {}

/** Refresh de başarısız olduğunda çağrılır (router login'e yönlendirir). */
export function setUnauthorizedHandler(handler: UnauthorizedHandler): void {
  unauthorizedHandler = handler
}

type TokenRefreshedHandler = () => void

let tokenRefreshedHandler: TokenRefreshedHandler = () => {}

/** Süresi dolan access token arka planda yenilenince çağrılır (kimlik/izinler tazelenir). */
export function setTokenRefreshedHandler(handler: TokenRefreshedHandler): void {
  tokenRefreshedHandler = handler
}

/* --------------------------------------------------------------- istekler */

export type QueryValue = string | number | boolean | null | undefined | string[] | number[]

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE'
  body?: unknown
  query?: Record<string, QueryValue>
  signal?: AbortSignal
  /** `/auth/refresh` ve `/auth/login` kendi 401'lerini yeniden denemez. */
  skipAuthRefresh?: boolean
  /** Varsayılan `application/json`; dosya indirmede dosya türü. */
  accept?: string
}

export function buildQueryString(query: Record<string, QueryValue> | undefined): string {
  if (!query) return ''
  const params = new URLSearchParams()
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === '') continue
    if (Array.isArray(value)) {
      // Liste filtreleri tekrarlı parametre olarak gider: ?kantar_id=kantar1&kantar_id=kantar2
      for (const item of value) params.append(key, String(item))
    } else {
      params.append(key, String(value))
    }
  }
  const serialized = params.toString()
  return serialized ? `?${serialized}` : ''
}

/* ------------------------------------------------------- refresh kuyruğu */

export interface RefreshedSession {
  access_token: string
  expires_in: number
}

interface RefreshFlight {
  promise: Promise<RefreshedSession | null>
  /** 401 yolundan katılan varsa kimlik tazelenir; açılış yenilemesi kimliği zaten çeker. */
  notify: boolean
}

let refreshInFlight: RefreshFlight | null = null

/** Sekmeler arası kilit adı: refresh cookie'si tüm sekmelerde ortaktır. */
export const REFRESH_LOCK_NAME = 'betontas-auth-refresh'

/**
 * Refresh token her kullanımda döner; iki sekme aynı cookie'yi aynı anda gönderirse
 * sunucu ikincisini yeniden kullanım sayar. Web Locks ile sekmeler sırayla yeniler:
 * kilidi bekleyen sekme, öncekinin kurduğu yeni cookie'yle gider.
 */
async function withRefreshLock<T>(task: () => Promise<T>): Promise<T> {
  const locks = typeof navigator === 'undefined' ? undefined : navigator.locks
  if (!locks) return task()
  return await locks.request(REFRESH_LOCK_NAME, task)
}

async function performRefresh(flight: RefreshFlight): Promise<RefreshedSession | null> {
  try {
    const response = await withRefreshLock(() =>
      fetch(`${env.apiBaseUrl}/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
        headers: { Accept: 'application/json' },
      }),
    )
    if (!response.ok) return null
    const payload = (await response.json()) as Partial<RefreshedSession>
    if (typeof payload.access_token !== 'string') return null
    tokenStore.set(payload.access_token)
    if (flight.notify) tokenRefreshedHandler()
    return { access_token: payload.access_token, expires_in: payload.expires_in ?? 0 }
  } catch {
    return null
  }
}

function joinRefresh(notify: boolean): Promise<RefreshedSession | null> {
  if (!refreshInFlight) {
    const flight: RefreshFlight = { promise: Promise.resolve(null), notify }
    flight.promise = performRefresh(flight).finally(() => {
      refreshInFlight = null
    })
    refreshInFlight = flight
  } else if (notify) {
    refreshInFlight.notify = true
  }
  return refreshInFlight.promise
}

/**
 * Eşzamanlı 401'lerde tek refresh çalışır; diğer istekler aynı söze bağlanır.
 */
export async function refreshAccessToken(): Promise<boolean> {
  return (await joinRefresh(true)) !== null
}

/** Açılış yenilemesi: 401 yolundaki yenilemeyle aynı kuyruğu ve kilidi paylaşır. */
export function refreshSession(): Promise<RefreshedSession | null> {
  return joinRefresh(false)
}

/* ------------------------------------------------------------ apiFetch */

async function readBody(response: Response): Promise<unknown> {
  if (response.status === 204) return null
  const text = await response.text()
  if (!text) return null
  try {
    return JSON.parse(text)
  } catch {
    return text
  }
}

async function sendOnce(path: string, options: RequestOptions): Promise<Response> {
  const headers: Record<string, string> = { Accept: options.accept ?? 'application/json' }
  const token = tokenStore.get()
  if (token) headers.Authorization = `Bearer ${token}`
  // Dosya yükleme: FormData olduğu gibi gider, sınır (boundary) başlığını tarayıcı yazar.
  const form = options.body instanceof FormData
  if (options.body !== undefined && !form) headers['Content-Type'] = 'application/json'

  return fetch(`${env.apiBaseUrl}${path}${buildQueryString(options.query)}`, {
    method: options.method ?? 'GET',
    credentials: 'include',
    headers,
    body:
      options.body === undefined
        ? undefined
        : form
          ? (options.body as FormData)
          : JSON.stringify(options.body),
    signal: options.signal,
  })
}

/** 401'de bir kez refresh + yeniden deneme; yanıtın gövdesi çağırana bırakılır. */
async function requestWithRefresh(path: string, options: RequestOptions): Promise<Response> {
  let response: Response
  try {
    response = await sendOnce(path, options)
  } catch (error) {
    throw new ApiError(networkProblem(error instanceof Error ? error.message : undefined))
  }

  if (response.status === 401 && !options.skipAuthRefresh) {
    const refreshed = await refreshAccessToken()
    if (refreshed) {
      try {
        response = await sendOnce(path, options)
      } catch (error) {
        throw new ApiError(networkProblem(error instanceof Error ? error.message : undefined))
      }
    } else {
      tokenStore.clear()
      unauthorizedHandler()
    }
  }
  return response
}

/**
 * Tek HTTP giriş noktası.
 *
 * ENTEGRASYON NOTU (mock → gerçek API, adım 2):
 * `pnpm gen:api` ile `src/types/api.d.ts` üretildikten sonra bu fonksiyonun gövdesi
 * `openapi-fetch` istemcisine devredilir:
 *
 *   import createClient from 'openapi-fetch'
 *   import type { paths } from '@/types/api'
 *   export const client = createClient<paths>({ baseUrl: env.apiBaseUrl, credentials: 'include' })
 *   client.use(authMiddleware)   // aşağıdaki token + refresh mantığı middleware'e taşınır
 *
 * `src/services/http/*.ts` dosyaları yalnızca `apiFetch` çağırır; imza korunursa
 * servislerde değişiklik gerekmez.
 */
export async function apiFetch<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const response = await requestWithRefresh(path, options)
  const payload = await readBody(response)

  if (!response.ok) {
    throw new ApiError(parseProblem(payload, response.status))
  }

  return payload as T
}

/**
 * Dosya indirme (`accept` = dosya türü): Authorization/refresh `apiFetch` ile aynı. Başarıda
 * gövde `Blob`; hata gövdesi problem+json olarak `ApiError` olur.
 */
export async function apiFetchBlob(
  path: string,
  options: RequestOptions = {},
): Promise<{ blob: Blob; contentDisposition: string | null }> {
  const response = await requestWithRefresh(path, options)
  if (!response.ok) {
    throw new ApiError(parseProblem(await readBody(response), response.status))
  }
  return {
    blob: await response.blob(),
    contentDisposition: response.headers.get('Content-Disposition'),
  }
}
